import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormField, SitecoreForm } from '@sitecore-jss/sitecore-jss-forms';
import { FormResult } from '@sitecore-jss/sitecore-jss-forms/types/FormResult';
import { JssFormData } from '@sitecore-jss/sitecore-jss-forms/types/JssFormData';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { Url } from 'next/dist/shared/lib/router/router';
import { NextRouter } from 'next/router';

import { AfterSubmitFormMessage, CrmFormEntity, FormMessageType } from '@gn-audio/jss-core';

import { getApiKeyForSite, getApiPrefix, getPageNameFromUrl, getSiteNameFromUrl } from 'lib/utils';
import { useEnvConfig } from 'src/contexts/EnvConfigContext';

import { CustomFieldTypes } from '../components/customFieldTypes';
import { FormNavigationStep, NAV_STEP_ATTRIBUTE } from '../constants/formNavigation';
import { useFormState } from '../contexts/FormState/FormStateContext';
import { useFieldValues } from '../contexts/FormValues/FieldValuesContext';
import { useGoogleRecaptchaManager } from '../contexts/GoogleRecaptchaManagerContext';
import { JabraFormModel } from '../types/jabraFormModel';
import { postFormMessage, postToastMessage } from '../utilities';
import {
  clearCampaignParamsCookie,
  getCampaignParamValue,
} from '../utilities/campaignParamsCookie';
import { buildUserInfoCRM } from '../utilities/crmUserInfo';
import { sitecoreFormTheme } from '../utilities/getFormTheme';
import { getGoogleAnalyticsClientId } from '../utilities/googleAnalytics';

const ANTI_FORGERY_TOKEN_FIELD_NAME = '__RequestVerificationToken';

const findAntiForgeryToken = (obj: unknown): string | null => {
  if (obj === null || typeof obj !== 'object') return null;

  const record = obj as Record<string, unknown>;
  if (record.antiForgeryToken && typeof record.antiForgeryToken === 'object') {
    const token = record.antiForgeryToken as Record<string, unknown>;
    if (token.name === ANTI_FORGERY_TOKEN_FIELD_NAME && typeof token.value === 'string') {
      return token.value;
    }
  }

  for (const value of Object.values(record)) {
    const found = findAntiForgeryToken(value);
    if (found) return found;
  }

  return null;
};

const hasSubmitButton = (fields: FormField[]): boolean =>
  fields.some(
    (field) =>
      field.model?.fieldTypeItemId === CustomFieldTypes.Button ||
      (Array.isArray(field.fields) && hasSubmitButton(field.fields as FormField[]))
  );

const isFinalSubmissionSuccess = (result: FormResult): boolean => {
  return result.success && (!result.nextForm || !hasSubmitButton(result.nextForm.fields));
};

interface UseFormControllerArgs {
  fields: SitecoreForm;
  router: NextRouter;
  onNextForm: (fields: FormField[]) => void;
}

export const useFormController = ({ fields, router, onNextForm }: UseFormControllerArgs) => {
  const { updateFieldValue, hiddenItemIds, fieldValues } = useFieldValues();
  const { supportApiKey, storeApiKey, tieredServicePortalApiKey } = useEnvConfig();

  const {
    cssClass,
    localScriptTop,
    globalScriptTop,
    localScriptBottom,
    globalScriptBottom,
    localCss,
    globalCss,
    itemId,
    align: alignmentStyle,
    customFormId,
    actionAttributes,
    theme,
  } = fields.metadata as JabraFormModel;

  const crmEntity = actionAttributes['data-crm-entity'];
  const isCrmForm = Boolean(crmEntity);

  const { sitecoreContext } = useSitecoreContext();
  const formTheme = sitecoreContext?.route?.fields?.formTheme as sitecoreFormTheme | undefined;

  const { setBusyButtonId } = useFormState();
  const clickedSubmitButtonId = useRef<string | null>(null);
  const clickedStepDirection = useRef<number>(1);
  const refreshedAntiForgeryToken = useRef<string | null>(null);
  const [pageIndex, setPageIndex] = useState(1);

  const siteName = getSiteNameFromUrl(router.asPath);
  const apiPrefix: string = getApiPrefix(getPageNameFromUrl(router.asPath));
  const apiKey = getApiKeyForSite(siteName, {
    storeApiKey,
    supportApiKey,
    tieredServicePortalApiKey,
  });

  const processPlaceholders = (content: string, formId: string): string => {
    return !!content ? content.replace(/\[\[FORMID\]\]/g, formId).replace(/<br\/>/g, '') : '';
  };

  const scripts = {
    localScriptTop: processPlaceholders(localScriptTop, itemId),
    globalScriptTop: processPlaceholders(globalScriptTop, itemId),
    localScriptBottom: processPlaceholders(localScriptBottom, itemId),
    globalScriptBottom: processPlaceholders(globalScriptBottom, itemId),
    localCss: processPlaceholders(localCss, itemId),
    globalCss: processPlaceholders(globalCss, itemId),
  };

  const onRedirect = (url: Url) => {
    if (window === window.parent) {
      router.push(url);
    } else {
      postFormMessage({
        type: FormMessageType.OnRedirect,
        url,
      });
    }
  };

  const { requestRecaptchaRefresh, setFormName } = useGoogleRecaptchaManager();
  useEffect(() => {
    setFormName(fields.metadata.name);
  }, [fields.metadata.name, setFormName]);

  const scheduleRecaptchaRefreshRequest = (validationErrorFields: string[] = []) => {
    window.setTimeout(() => {
      requestRecaptchaRefresh(validationErrorFields);
    }, 0);
  };

  const removeHiddenFieldsFromFormData = (formData: JssFormData) => {
    if (hiddenItemIds.size === 0) return;

    const allFormData = formData.get();
    const hiddenFieldPrefixes = new Map<string, string>();

    allFormData.forEach((item) => {
      const startIndex = item.key.indexOf('[');
      const endIndex = item.key.indexOf(']', startIndex);

      if (startIndex === -1 || endIndex === -1) return;

      const fieldItemId = item.key.slice(startIndex + 1, endIndex);
      if (!hiddenItemIds.has(fieldItemId)) return;

      formData.remove(item.key);
      if (!hiddenFieldPrefixes.has(fieldItemId)) {
        const prefix = item.key.substring(0, item.key.lastIndexOf('.'));
        hiddenFieldPrefixes.set(fieldItemId, prefix);
      }
    });

    hiddenFieldPrefixes.forEach((prefix) => {
      formData.set(`${prefix}.Required`, 'false');
    });
  };

  const postLeadFormSubmitMessage = () => {
    if (crmEntity !== CrmFormEntity.Lead) return;

    const userInfo = buildUserInfoCRM(fields.fields, fieldValues);
    const crmContact = Object.values(userInfo).some((v) => v !== '') ? userInfo : undefined;

    postFormMessage<AfterSubmitFormMessage>({
      type: FormMessageType.AfterSubmit,
      crmEntity,
      crmCampaignId: actionAttributes['data-crm-campaign-id'],
      crmCampaignName: actionAttributes['data-crm-campaign-name'],
      crmTopic: actionAttributes['data-crm-topic'],
      crmContact,
    });
  };

  const createUnexpectedErrorResult = (error: unknown): FormResult => {
    const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
    postToastMessage('error', errorMessage);

    return {
      success: false,
      errors: [errorMessage],
      validationErrors: {},
    };
  };

  const refreshAntiForgeryToken = async (): Promise<boolean> => {
    try {
      const formPageResponse = await fetch(window.location.href, { credentials: 'include' });
      if (!formPageResponse.ok) return false;

      const formPageHtml = await formPageResponse.text();
      const parser = new DOMParser();
      const formPageDoc = parser.parseFromString(formPageHtml, 'text/html');
      const nextDataScript = formPageDoc.getElementById('__NEXT_DATA__');

      if (!nextDataScript?.textContent) return false;

      const formPageNextData = JSON.parse(nextDataScript.textContent);
      const antiForgeryToken = findAntiForgeryToken(formPageNextData);

      if (!antiForgeryToken) return false;

      refreshedAntiForgeryToken.current = antiForgeryToken;
      return true;
    } catch {
      return false;
    }
  };

  const handlePotentialAntiForgeryValidationFail = async (): Promise<FormResult> => {
    const isTokenRefreshed = await refreshAntiForgeryToken();
    scheduleRecaptchaRefreshRequest();

    const errorMessage = isTokenRefreshed
      ? 'Your session was broken but has been updated automatically. Please submit the form again.'
      : 'Your session has expired. Please reload the page and try again.';
    postToastMessage('error', errorMessage);

    return {
      success: false,
      errors: [errorMessage],
      validationErrors: {},
    };
  };

  const handleFormResponse = async (response: Response): Promise<FormResult> => {
    if (response.ok) {
      postLeadFormSubmitMessage();
      clearCampaignParamsCookie();
    }

    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) {
      return handlePotentialAntiForgeryValidationFail();
    }

    const result: FormResult = await response.json();
    if (!response.ok || !result.success) {
      scheduleRecaptchaRefreshRequest(Object.keys(result.validationErrors || {}));
      const errorMessages = result.errors?.join('. ') || 'Form submission failed';
      postToastMessage('error', errorMessages);
    } else {
      // Check if this is the final submission (no more pages to navigate to, or showing thank you page)
      const isFinalSuccess = isFinalSubmissionSuccess(result);
      if (isFinalSuccess) {
        postToastMessage('info', 'Form submitted successfully');
      }
    }

    if (result.nextForm) {
      onNextForm(result.nextForm.fields);
      const stepDirection = clickedStepDirection.current;
      setPageIndex((current) => Math.max(1, current + stepDirection));
    }

    return result;
  };

  const customFormFetcher = async (
    formData: JssFormData,
    endpoint: string
  ): Promise<FormResult> => {
    if (clickedSubmitButtonId.current) {
      setBusyButtonId(clickedSubmitButtonId.current);
    }

    removeHiddenFieldsFromFormData(formData);

    if (isCrmForm && crmEntity !== CrmFormEntity.Nps && crmEntity !== CrmFormEntity.Newsletter) {
      formData.set('gnh_medium', getCampaignParamValue('utm_medium') ?? '');
      formData.set('gnh_source', getCampaignParamValue('utm_source') ?? '');
      formData.set('gnh_user_pseudo', getGoogleAnalyticsClientId() ?? '');
    }

    if (refreshedAntiForgeryToken.current) {
      formData.set(ANTI_FORGERY_TOKEN_FIELD_NAME, refreshedAntiForgeryToken.current);
    }

    try {
      const response = await fetch(endpoint, {
        body: formData.toMultipartFormData(),
        method: 'post',
        credentials: 'include',
      });

      if (response.redirected) {
        return handlePotentialAntiForgeryValidationFail();
      }

      return handleFormResponse(response);
    } catch (error) {
      scheduleRecaptchaRefreshRequest();
      return createUnexpectedErrorResult(error);
    } finally {
      setBusyButtonId(null);
      clickedStepDirection.current = 1;
    }
  };

  const formRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleFormClick = (event: Event) => {
      let clickedElement = event.target as HTMLElement;

      while (clickedElement && clickedElement.tagName !== 'BUTTON') {
        clickedElement = clickedElement.parentElement as HTMLElement;
        if (!clickedElement) break;
      }

      const clickedButton = clickedElement as HTMLButtonElement;
      if (clickedButton?.type === 'submit') {
        clickedSubmitButtonId.current = clickedButton.id;
        clickedStepDirection.current =
          clickedButton.getAttribute(NAV_STEP_ATTRIBUTE) === FormNavigationStep.Previous ? -1 : 1;
      }
    };

    const formElement = formRef.current;
    if (!formElement) return;

    formElement.addEventListener('click', handleFormClick, true);
    return () => {
      formElement.removeEventListener('click', handleFormClick, true);
    };
  }, []);

  useEffect(() => {
    const communicateNewFormHeight = () => {
      const formHeight = formRef.current?.scrollHeight ?? document.body.scrollHeight;
      postFormMessage({
        type: FormMessageType.SetHeight,
        height: formHeight,
      });
    };

    const communicateFormId = () =>
      postFormMessage({
        type: FormMessageType.SetFormId,
        formId: customFormId,
      });

    communicateNewFormHeight();
    communicateFormId();

    if (!formRef.current) return;
    const resizeObserver = new ResizeObserver(communicateNewFormHeight);
    resizeObserver.observe(formRef.current);
    return () => resizeObserver.disconnect();
  }, [customFormId]);

  const handleFieldChange = useCallback(
    (key: string, value: string | string[] | File[]) => {
      const fieldItemId = key.slice(key.indexOf('[') + 1, key.lastIndexOf(']'));

      if (Array.isArray(value) && value.length > 0 && value[0] instanceof File) {
        const fileNames = (value as File[]).map((f) => f.name).join(',');
        updateFieldValue(fieldItemId, fileNames);
        return;
      }
      updateFieldValue(fieldItemId, value as string | string[]);
    },
    [updateFieldValue]
  );

  const sharedFormProps = useMemo(
    () => ({
      language: router.locale,
      sitecoreApiHost: apiPrefix,
      sitecoreApiKey: apiKey,
    }),
    [router.locale, apiPrefix, apiKey]
  );

  return {
    formRef,
    cssClass,
    alignmentStyle,
    customFormId,
    actionAttributes,
    theme,
    formTheme,
    scripts,
    sharedFormProps,
    pageIndex,
    onRedirect,
    customFormFetcher,
    handleFieldChange,
  };
};
